import React from 'react';
import apiClient from '../../apiClient';
import { withRouter } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core';
import AddBoxIcon from '@material-ui/icons/AddBox';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';
import '../ItemCategory/ItemCategory.scss';

class EntityGroup extends React.Component {
  state = {
    entityGroups: [],
    newName: '',
    editGroup: null,
    editName: '',
    deleteGroup: null,
    successMessage: '',
    errorMessage: '',
  };

  componentDidMount() {
    const user = JSON.parse(sessionStorage.getItem('user'));
    if (
      !user ||
      !Array.isArray(user.permissions) ||
      !user.permissions.some(
        (permission) =>
          permission.PermissionName === 'Item Category' && permission.PermissionAllowed === 1
      )
    ) {
      this.props.history.push('/home');
      return;
    }
    this.loadEntityGroups();
  }

  getAuthConfig = () => {
    const user = JSON.parse(sessionStorage.getItem('user')) || {};
    return { headers: { Authorization: `Bearer ${user.accessToken || ''}` } };
  };

  loadEntityGroups = () => {
    apiClient
      .get('/api/get/entitygroup', this.getAuthConfig())
      .then((res) => this.setState({ entityGroups: res.data || [], errorMessage: '' }))
      .catch((err) => this.showError(err, 'Unable to load entity groups.'));
  };

  showError = (err, fallback) => {
    const message = err && err.response && err.response.data && err.response.data.error;
    this.setState({ errorMessage: message || fallback, successMessage: '' });
  };

  showSuccess = (message) => {
    this.setState({ successMessage: message, errorMessage: '' });
  };

  handleCreate = () => {
    const entityGroupName = this.state.newName.trim();
    if (!entityGroupName) {
      this.setState({ errorMessage: 'Entity group name is required.' });
      return;
    }
    apiClient
      .post('/api/post/entityGroups', [{ entityGroupName }], this.getAuthConfig())
      .then(() => {
        this.setState({ newName: '' });
        this.showSuccess('Entity group created.');
        this.loadEntityGroups();
      })
      .catch((err) => this.showError(err, 'Unable to create entity group.'));
  };

  openEdit = (group) => {
    this.setState({ editGroup: group, editName: group.EntityGroupName || '' });
  };

  handleUpdate = () => {
    const entityGroupName = this.state.editName.trim();
    if (!entityGroupName) {
      this.setState({ errorMessage: 'Entity group name is required.' });
      return;
    }
    apiClient
      .post(
        '/api/post/entityGroupUpdate',
        { entityGroupId: this.state.editGroup.EntityGroupId, entityGroupName },
        this.getAuthConfig()
      )
      .then(() => {
        this.setState({ editGroup: null, editName: '' });
        this.showSuccess('Entity group updated.');
        this.loadEntityGroups();
      })
      .catch((err) => this.showError(err, 'Unable to update entity group.'));
  };

  handleDelete = () => {
    apiClient
      .delete('/api/delete/entityGroup', {
        ...this.getAuthConfig(),
        params: { entityGroupId: this.state.deleteGroup.EntityGroupId },
      })
      .then(() => {
        this.setState({ deleteGroup: null });
        this.showSuccess('Entity group deleted. Categories using it are now ungrouped.');
        this.loadEntityGroups();
      })
      .catch((err) => this.showError(err, 'Unable to delete entity group.'));
  };

  render() {
    return (
      <div className="ItemCategory">
        <h2>Entity Groups</h2>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, margin: '20px 0' }}>
          <TextField
            label="Entity Group Name"
            value={this.state.newName}
            onChange={(event) => this.setState({ newName: event.target.value })}
            onKeyDown={(event) => {
              if (event.key === 'Enter') this.handleCreate();
            }}
          />
          <Button color="primary" variant="contained" onClick={this.handleCreate}>
            <AddBoxIcon style={{ marginRight: 6 }} /> Add
          </Button>
        </div>

        {this.state.successMessage && (
          <div style={{ backgroundColor: '#28a745', color: '#fff', padding: 10, marginBottom: 10 }}>
            {this.state.successMessage}
          </div>
        )}
        {this.state.errorMessage && (
          <div style={{ backgroundColor: '#FF7F7F', color: '#fff', padding: 10, marginBottom: 10 }}>
            {this.state.errorMessage}
          </div>
        )}

        <Table size="small" aria-label="Entity groups">
          <TableHead>
            <TableRow>
              <TableCell>Entity Group Name</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {this.state.entityGroups.map((group) => (
              <TableRow key={group.EntityGroupId}>
                <TableCell>{group.EntityGroupName}</TableCell>
                <TableCell align="right">
                  <IconButton aria-label="Edit entity group" onClick={() => this.openEdit(group)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    aria-label="Delete entity group"
                    onClick={() => this.setState({ deleteGroup: group })}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Dialog open={Boolean(this.state.editGroup)} onClose={() => this.setState({ editGroup: null })}>
          <DialogTitle>
            Edit Entity Group
            <IconButton
              aria-label="Close"
              onClick={() => this.setState({ editGroup: null })}
              style={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="Entity Group Name"
              value={this.state.editName}
              onChange={(event) => this.setState({ editName: event.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ editGroup: null })}>Cancel</Button>
            <Button color="primary" variant="contained" onClick={this.handleUpdate}>Save</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(this.state.deleteGroup)}
          onClose={() => this.setState({ deleteGroup: null })}
        >
          <DialogTitle>Delete Entity Group</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Delete {this.state.deleteGroup && this.state.deleteGroup.EntityGroupName}? Categories
              assigned to this group will become ungrouped.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ deleteGroup: null })}>Cancel</Button>
            <Button color="secondary" onClick={this.handleDelete}>Delete</Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

export default withRouter(EntityGroup);
